import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import {
  BadgeCheck, Shield, CheckCircle, Calendar,
  Globe, RefreshCw, ExternalLink, AlertTriangle,
  Search, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";

const WORKER_URL = "https://api.transparencybadge.com";

interface BadgeData {
  status: "active" | "expired" | "suspended" | "pending_review" | "not_found";
  domain?: string;
  category?: string;
  description?: string;
  verifiedAt?: string;
  expiresAt?: string;
  manualReviewPassed?: boolean;
  domainAgeMonths?: number;
  domainAgeYear?: number;
  serverLocation?: { country: string; organization?: string };
  https?: boolean;
  sslStatus?: "valid" | "not_found" | "unknown";
  lastCheckedAt?: string;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric"
    });
  } catch { return dateStr; }
}

export default function VerifyBadge() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<BadgeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setData({ status: "not_found" }); setLoading(false); return; }

    fetch(`${WORKER_URL}/api/badge/verify/${token}`)
      .then((r) => r.json())
      .then((json) => setData(json))
      .catch(() => setData({ status: "not_found" }))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <Layout>
        <Helmet><title>Verifying Badge — Universal Website Transparency Badge</title></Helmet>
        <section className="py-24 container max-w-lg mx-auto text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Verifying badge...</p>
        </section>
      </Layout>
    );
  }

  if (!data || data.status === "not_found") {
    return (
      <Layout>
        <Helmet><title>Badge Not Found — Universal Website Transparency Badge</title></Helmet>
        <section className="py-24 container max-w-lg mx-auto text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Badge Not Found</h1>
          <p className="text-muted-foreground">This badge token is invalid or no longer exists. If you believe this is an error, please contact the website owner.</p>
          <Button asChild variant="outline"><Link to="/">Go to Homepage</Link></Button>
        </section>
      </Layout>
    );
  }

  if (data.status === "expired" || data.status === "suspended") {
    return (
      <Layout>
        <Helmet><title>Badge Expired — Universal Website Transparency Badge</title></Helmet>
        <section className="py-24 container max-w-lg mx-auto text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-caution/10 flex items-center justify-center mx-auto">
            <AlertTriangle className="h-8 w-8 text-caution" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Badge {data.status === "expired" ? "Expired" : "Suspended"}</h1>
          <p className="text-muted-foreground">
            The verification badge for <span className="font-medium text-foreground">{data.domain}</span> is no longer active.
            The website owner may have cancelled their subscription.
          </p>
          <Button asChild variant="outline">
            <Link to={`/?check=${data.domain}`}>
              <Search className="mr-2 h-4 w-4" />Check This Domain
            </Link>
          </Button>
        </section>
      </Layout>
    );
  }

  if (data.status === "pending_review") {
    return (
      <Layout>
        <Helmet><title>Badge Pending — Universal Website Transparency Badge</title></Helmet>
        <section className="py-24 container max-w-lg mx-auto text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-caution/10 flex items-center justify-center mx-auto">
            <Loader2 className="h-8 w-8 text-caution" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Review in Progress</h1>
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">{data.domain}</span> has applied for verification and is currently being reviewed by our team. Check back soon.
          </p>
          <Button asChild variant="outline"><Link to="/">Go to Homepage</Link></Button>
        </section>
      </Layout>
    );
  }

  // Active badge
  return (
    <Layout>
      <Helmet>
        <title>{data.domain} — Verified · Universal Website Transparency Badge</title>
        <meta name="description" content={`${data.domain} is verified by Universal Website Transparency Badge. Passed automatic and manual safety checks.`} />
      </Helmet>

      {/* Verified hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-safe-bg to-background">
        <div className="container py-16 md:py-20 max-w-3xl mx-auto text-center space-y-6">
          <div className="relative inline-flex items-center justify-center">
            <div className="absolute inset-0 bg-safe/20 rounded-full blur-2xl scale-150" />
            <div className="relative w-20 h-20 rounded-full bg-safe/10 border-2 border-safe/30 flex items-center justify-center animate-pulse-soft">
              <BadgeCheck className="h-10 w-10 text-safe" />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-safe uppercase tracking-wider">✓ Verified Website</p>
            <div className="flex items-center justify-center gap-2.5">
              <img
                src={`https://www.google.com/s2/favicons?domain=${data.domain}&sz=64`}
                alt=""
                className="w-8 h-8 rounded-md"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">{data.domain}</h1>
            </div>
            <p className="text-muted-foreground text-lg">
              This website has been verified by the{" "}
              <span className="font-medium text-foreground">Universal Website Transparency Badge</span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex flex-col items-center gap-1.5">
              <Button asChild size="lg" variant="outline">
                <a href={`https://${data.domain}`} target="_blank" rel="noopener noreferrer">
                  Visit {data.domain}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <span className="text-xs text-transparent select-none" aria-hidden="true">spacer</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <Button asChild size="lg">
                <Link to="/get-verified">
                  Get Your Badge
                  <BadgeCheck className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <span className="text-xs text-muted-foreground">1 month free · No card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Verification details */}
      <section className="py-14">
        <div className="container max-w-3xl mx-auto space-y-8">

          {/* Website description */}
          {data.description && (
            <div className="bg-card border rounded-2xl overflow-hidden shadow-soft">
              <div className="px-6 py-4 border-b bg-primary/5">
                <h2 className="font-semibold text-foreground">About This Website</h2>
              </div>
              <div className="px-6 py-5">
                <p className="text-sm text-muted-foreground leading-relaxed">{data.description}</p>
              </div>
            </div>
          )}

          {/* Tests passed */}
          <div className="bg-card border rounded-2xl overflow-hidden shadow-soft">
            <div className="px-6 py-4 border-b bg-safe-bg/50">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <Shield className="h-4 w-4 text-safe" />
                Safety Checks Passed
              </h2>
            </div>
            <div className="divide-y">
              {[
                ...(data.https && data.sslStatus === "valid" ? [
                  { label: "HTTPS & SSL encryption verified", desc: "Live check confirms the site loads securely with a valid certificate", type: "auto" }
                ] : []),
                { label: "Automatic threat intelligence scan", desc: "Domain checked against multiple public threat databases", type: "auto" },
                { label: "Eligibility check by our team", desc: "Application reviewed against our published criteria before approval", type: "manual" },
                { label: "No malware or phishing signals detected", desc: "Clean across all monitored threat sources", type: "auto" },
                { label: "Domain reputation check passed", desc: "Domain history and age verified", type: "auto" },
                { label: "Active monitoring continued", desc: "This website is re-scanned regularly while the badge is active", type: "auto" },
              ].map((check) => (
                <div key={check.label} className="flex items-start gap-4 px-6 py-4">
                  <CheckCircle className="h-5 w-5 text-safe shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{check.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{check.desc}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                    check.type === "manual" ? "bg-primary/10 text-primary" : "bg-safe-bg text-safe"
                  }`}>
                    {check.type === "manual" ? "Manual" : "Automatic"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Domain info */}
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Globe, label: "Domain", value: data.domain },
              { icon: Globe, label: "Category", value: data.category },
              { icon: Calendar, label: "Verified Since", value: formatDate(data.verifiedAt) },
              { icon: RefreshCw, label: "Valid Until", value: formatDate(data.expiresAt) },
              ...(data.domainAgeMonths ? [{
                icon: Calendar,
                label: "Domain Age",
                value: data.domainAgeYear
                  ? `${Math.floor(data.domainAgeMonths / 12)} years (Registered ${data.domainAgeYear})`
                  : `${Math.floor(data.domainAgeMonths / 12)} years`,
                note: data.domainAgeMonths < 24
                  ? "New domains are common for growing businesses and are weighed alongside our eligibility check."
                  : undefined
              }] : []),
              ...(data.serverLocation ? [{
                icon: Globe,
                label: "Server Location (approx)",
                value: data.serverLocation.organization
                  ? `${data.serverLocation.country} — ${data.serverLocation.organization}`
                  : data.serverLocation.country,
                note: "Based on IP geolocation"
              }] : []),
            ].map((item) => (
              <div key={item.label} className="bg-card border rounded-xl p-5 flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-sm font-medium text-foreground">{item.value}</p>
                  {item.note && (
                    <p className="text-xs text-muted-foreground/80 mt-0.5">{item.note}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Trust statement */}
          <div className="bg-accent/50 border rounded-xl p-5 space-y-2">
            <p className="text-sm font-medium text-foreground">About this verification</p>
            <p className="text-sm text-muted-foreground">
              This badge is maintained on a monthly basis. Our team continuously monitors verified websites.
              If <span className="font-medium">{data.domain}</span> is found to violate our safety standards
              at any time, the badge is immediately revoked and this page will reflect that.
            </p>
            {data.lastCheckedAt && (
              <p className="text-xs text-muted-foreground/80 pt-1">
                Last checked: {formatDate(data.lastCheckedAt)}
              </p>
            )}
          </div>

          {/* CTA */}
          <div className="text-center space-y-3 pt-4 border-t">
            <p className="text-sm text-muted-foreground">Want to verify your own website?</p>
            <Button asChild>
              <Link to="/get-verified">
                Get Your Badge
                <BadgeCheck className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground">1 month free · No card required</p>
            <p className="text-xs text-muted-foreground">
              Powered by{" "}
              <Link to="/" className="text-primary underline underline-offset-2">
                Universal Website Transparency Badge
              </Link>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
